import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { useMenuLabel } from '@/lib/menu-labels';

const DropshopPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const collections = useAdminStore((s) => s.dropshopCollections);
  const menuLabel = useMenuLabel();

  const activeCollections = collections.filter((c) => c.isActive);

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Hero */}
      <div className="pt-24 sm:pt-32 pb-10 px-4 sm:px-6 md:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-5xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none mb-4">
              {menuLabel('dropshop', t.dropshop.title)}
            </h1>
            <p className="text-sm sm:text-base text-black/40 dark:text-white/40 max-w-xl">
              {t.dropshop.subtitle}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Collections Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-24 pb-20">
        {activeCollections.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-black/30 dark:text-white/30 text-sm">{t.dropshop.noCollections}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {activeCollections.map((collection, i) => {
              const artistName = typeof collection.artist === 'string'
                ? collection.artist
                : localize(collection.artist.name);

              const releaseDate = new Date(collection.releaseDate).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              });

              return (
                <motion.div
                  key={collection._id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                >
                  <Link
                    to={`/dropshop/${collection.slug}`}
                    className="group block"
                  >
                    {/* Cover Image */}
                    <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#f5f5f5] dark:bg-[#111] mb-4">
                      <img
                        src={collection.coverImage}
                        alt={localize(collection.title)}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />

                      {/* Limited Edition Badge */}
                      <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-full">
                        <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-black dark:text-white">
                          {t.dropshop.limitedEdition}
                        </span>
                      </div>

                      {/* Bottom overlay info */}
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/60 mb-1">
                          {artistName}
                        </p>
                        <h3 className="text-lg sm:text-xl font-syncopate font-bold text-white uppercase tracking-tight leading-tight">
                          {localize(collection.title)}
                        </h3>
                      </div>
                    </div>

                    {/* Info below image */}
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2 text-[9px] text-black/30 dark:text-white/30">
                        <Calendar size={11} />
                        <span className="font-medium">{releaseDate}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 group-hover:text-black dark:group-hover:text-white transition-colors">
                        {t.dropshop.explore}
                        <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DropshopPage;
